// Admin · Activity tab — timeline of last 100 platform events.
import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, Crown, ShieldAlert, CheckCircle2 } from "lucide-react";

import { API, safeJson } from "../../../lib/api";

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `hace ${days}d`;
  return d.toLocaleDateString();
};
const fmtAbs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
};

const ICONS = {
  nuevo_usuario:   { Icon: UserPlus,      color: "#00d084" },
  nuevo_pro:       { Icon: Crown,         color: "#f97316" },
  alerta_generada: { Icon: ShieldAlert,   color: "#ff3b5c" },
  alerta_resuelta: { Icon: CheckCircle2,  color: "#aaa" },
};

export default function ActivityTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API}/admin/activity-log`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await safeJson(r);
        if (!alive) return;
        if (r.ok && body) { setData(body); setErr(null); }
        else { setErr(body?.detail || `HTTP ${r.status}`); }
      } catch (e) {
        if (alive) setErr(String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return (
    <div className="flex items-center gap-2 font-mono text-[11px] text-white/55">
      <Loader2 className="h-4 w-4 animate-spin" /> Cargando timeline…
    </div>
  );
  if (err) return <p className="font-mono text-[11px] text-[#ff3b5c]">Error: {err}</p>;
  if (!data) return null;

  const eventos = data.eventos || [];

  return (
    <div data-testid="admin-tab-activity" className="space-y-2">
      <p className="font-mono text-[10px] uppercase tracking-[2px] text-white/55">
        Últimos {eventos.length} eventos
      </p>
      {eventos.length === 0 ? (
        <p className="font-mono text-[11px] text-white/45">Sin actividad reciente.</p>
      ) : (
        <ul className="relative space-y-1.5 border-l border-white/10 pl-4">
          {eventos.map((ev, idx) => {
            const meta = ICONS[ev.tipo] || { Icon: ShieldAlert, color: "#aaa" };
            const { Icon, color } = meta;
            return (
              <li
                key={`${ev.tipo}-${ev.timestamp}-${idx}`}
                className="relative flex items-start gap-2 rounded-md py-1 font-mono text-[11px]"
                data-testid="admin-activity-event"
              >
                <span
                  className="absolute -left-[20px] top-1.5 inline-flex h-3 w-3 items-center justify-center rounded-full"
                  style={{ background: color }}
                >
                  <Icon className="h-2 w-2 text-black" />
                </span>
                <div className="flex-1">
                  <p className="text-white/80">{ev.descripcion}</p>
                  <p className="text-[9px] text-white/35" title={fmtAbs(ev.timestamp)}>
                    {fmtRelative(ev.timestamp)} · <span className="text-white/25">{ev.tipo}</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
